import React from "react";
import { Hammer } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { AppLink } from "../../../../components/ui/link";
import type { RouteMap } from "../../types";

type LandingFooterProps = {
  loggedIn: boolean;
  hasBusiness: boolean;
  hasOrganization?: boolean;
  canAccessBackend?: boolean;
  routes: RouteMap;
  scrollToSection: (id: string) => void;
};

export const LandingFooter: React.FC<LandingFooterProps> = ({
  loggedIn,
  hasBusiness,
  hasOrganization,
  canAccessBackend,
  routes,
  scrollToSection,
}) => {
  const productAuthLink = loggedIn
    ? hasBusiness && canAccessBackend !== false
      ? { to: routes.dashboard, label: "Dashboard" }
      : hasOrganization && canAccessBackend
        ? { to: routes.onboarding, label: "Home" }
        : hasOrganization
          ? { to: routes.teammateHome ?? "/teammate", label: "Home" }
          : {
              to: routes.onboarding,
              label: "Continue setup",
            }
    : { to: routes.login, label: "Log in" };

  return (
    <footer className="bg-neutral-900 pt-16 pb-8 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-10 mb-16 sm:flex-row sm:gap-12 sm:items-start">
          <div className="w-full sm:w-[30%] sm:shrink-0 sm:min-w-0">
            <Button
              type="button"
              variant="ghost"
              onClick={() => scrollToSection("home")}
              className="flex items-center mb-6 h-auto p-0 hover:bg-transparent"
            >
              <div className="mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-premium-500 to-premium-600 shadow-lg shadow-premium-500/20">
                <Hammer className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-display font-bold text-2xl tracking-tight text-white">
                Tool<span className="text-premium-400">Kit</span>
              </span>
            </Button>
          </div>

          <div className="min-w-0 flex-1 grid grid-cols-2 gap-6 sm:grid-cols-3 sm:gap-8 lg:gap-12">
            <div className="min-w-0">
              <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">
                Product
              </h4>
              <ul className="space-y-3 text-neutral-500 text-sm font-medium">
                <li>
                  <AppLink
                    to="/website"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Website
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/job-photos"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Job photos
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/credentials"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Credentials
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/features"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Features
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/why-toolkit"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Why ToolKit?
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/pricing"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Pricing
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/testimonials"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Testimonials
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/different"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    How we&apos;re different
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/contractor-sites"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Live contractor sites
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to={productAuthLink.to}
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    {productAuthLink.label}
                  </AppLink>
                </li>
              </ul>
            </div>

            <div className="min-w-0">
              <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">
                Extras
              </h4>
              <ul className="space-y-3 text-neutral-500 text-sm font-medium">
                <li>
                  <AppLink
                    to={routes.terms}
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Terms of Service
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to={routes.privacy}
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Privacy Policy
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to={routes.faq}
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    FAQ
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/blog"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Blog
                  </AppLink>
                </li>
              </ul>
            </div>

            <div className="min-w-0">
              <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">
                Trades
              </h4>
              <ul className="space-y-3 text-neutral-500 text-sm font-medium">
                <li>
                  <AppLink
                    to="/trades"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    All trades
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/for/plumbers"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Plumbers
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/for/electricians"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Electricians
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/for/hvac"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    HVAC Techs
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/for/general-contractors"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    General Contractors
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/for/landscapers"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Landscapers
                  </AppLink>
                </li>
                <li>
                  <AppLink
                    to="/for/painters"
                    className="hover:text-premium-400 transition-colors text-neutral-500"
                  >
                    Painters
                  </AppLink>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-neutral-500 text-sm font-medium">
            <span suppressHydrationWarning>
              &copy; {new Date().getFullYear()} ToolKit. All rights reserved.
            </span>
          </p>
          <div className="flex gap-6 text-neutral-500">
            <a
              href="https://x.com/ToolKit_build"
              className="hover:text-white transition-colors text-sm font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              Twitter/X
            </a>
            <a
              href="https://www.linkedin.com/company/toolkit-build"
              className="hover:text-white transition-colors text-sm font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>
            <a
              href="https://www.instagram.com/toolkit.build"
              className="hover:text-white transition-colors text-sm font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              Instagram
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
